import { Identity } from '@/auth';
import { StorageService } from '@/services/storage';

type CredentialProfileBase = {
  id: string;
  label: string;
  identity: Identity;
  npi: string;
  shortlink?: string;
};

export type ProviderCredentialProfile = CredentialProfileBase & {
  identity: Identity.Provider;
  ssnLast4Digits: string;
  dob: string; // YYYY-MM-DD
};

export type InstitutionCredentialProfile = CredentialProfileBase & {
  // Institution users are always representatives in our auth flow.
  identity: Identity.Representative;
  representativeFirstName: string;
  representativeLastName: string;
};

export type CredentialProfile =
  | ProviderCredentialProfile
  | InstitutionCredentialProfile;

type CredentialProfilesFile = {
  profiles: CredentialProfile[];
};

const storage = new StorageService();

const SELECTED_PROFILE_KEY = 'DEV_SELECTED_CREDENTIAL_PROFILE';

const fileModules = import.meta.glob<CredentialProfilesFile>(
  '../dev-secrets.credentials.json',
  {
    eager: true,
    import: 'default',
  },
);

const credentialFile: CredentialProfilesFile | undefined =
  Object.values(fileModules)[0];

export function getCredentialProfilesFromFile(): CredentialProfile[] {
  return credentialFile?.profiles ?? [];
}

export function getSelectedCredentialProfile(): CredentialProfile | null {
  const raw = storage.getItem(SELECTED_PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CredentialProfile;
  } catch {
    return null;
  }
}

export function setSelectedCredentialProfile(profile: CredentialProfile) {
  storage.setItem(SELECTED_PROFILE_KEY, JSON.stringify(profile));
}

export function clearSelectedCredentialProfile() {
  storage.removeItem(SELECTED_PROFILE_KEY);
}

export function getCredentialValueForFieldId(
  fieldId: string,
  profile: CredentialProfile,
): string | undefined {
  const key = fieldId.trim().toLowerCase();

  if (key === 'npi') return profile.npi;
  if (key === 'ssnlast4digits' && profile.identity === Identity.Provider)
    return profile.ssnLast4Digits;
  if (key === 'dob' && profile.identity === Identity.Provider) return profile.dob;
  if (
    key === 'representativefirstname' &&
    profile.identity === Identity.Representative
  )
    return profile.representativeFirstName;
  if (
    key === 'representativelastname' &&
    profile.identity === Identity.Representative
  )
    return profile.representativeLastName;
  if (key === 'identity') return profile.identity;

  return undefined;
}
